import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { tokens } from "../theme";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  useUpdateStatusMutation,
  useGetFeedsMutation,
} from "../features/auth/apiSlice";
import { useSelector } from "react-redux";
import { selectSponserID } from "../features/auth/authSlice";

const ShowProposalsModal = ({
  setIsModalVisible,
  proposals,
  feedId,
  setFeeds,
}: {
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  proposals: any;
  feedId: string;
  setFeeds: React.Dispatch<any>;
}) => {
  const newProposals = useMemo(
    () =>
      proposals.map((proposal: any) => {
        return { ...proposal, ...proposal?.sentBy };
      }),
    [proposals]
  );
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [updateStatusApi] = useUpdateStatusMutation();
  const [getFeedsAPI] = useGetFeedsMutation();
  const sponserId = useSelector(selectSponserID);
  const [screenSize, setScreenSize] = useState(getCurrentDimension());
  const isNonMobile = useMediaQuery("(min-width:600px)");

  function getCurrentDimension() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(getCurrentDimension());
    };
    window.addEventListener("resize", updateDimension);

    return () => {
      window.removeEventListener("resize", updateDimension);
    };
  }, []);

  const handleUpdateStatus = async ({
    status,
    creatorId,
  }: {
    status: string;
    creatorId: string;
  }) => {
    const response: any = await updateStatusApi({
      feedId: feedId,
      status: status,
      sponserId: sponserId,
      creatorId: creatorId,
    }).unwrap();
    if (response.status === 200) {
      const response: any = await getFeedsAPI({ sponserId }).unwrap();
      setFeeds(response?.feeds);
    }
  };

  const columns = [
    { field: "_id", headerName: "ID" },
    {
      field: "name",
      headerName: "Name",
      cellClassName: "name-column--cell",
    },
    {
      field: "fbUrl",
      headerName: "Facebook URL",
    },
    {
      field: "youtubeUrl",
      headerName: "Youtube URL",
      flex: 1,
    },
    {
      field: "instaUrl",
      headerName: "Instagram URL",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params: any) => {
        return (
          <>
            {params.row.status === "pending" && (
              <div>
                <Button
                  variant="contained"
                  className="mr-[8px]"
                  style={{ backgroundColor: colors.greenAccent[600]}}
                  onClick={() =>
                    handleUpdateStatus({
                      status: "accepted",
                      creatorId: params.id,
                    })
                  }
                >
                  Accept
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: colors.redAccent[600] }}
                  onClick={() =>
                    handleUpdateStatus({
                      status: "rejected",
                      creatorId: params.id,
                    })
                  }
                >
                  Reject
                </Button>
              </div>
            )}
            {params.row.status === "accepted" && (
              <div>
                <Typography
                  variant="h5"
                  style={{ color: colors.greenAccent[400] }}
                >
                  Accepted
                </Typography>
              </div>
            )}
            {params.row.status === "rejected" && (
              <div>
                <Typography
                  variant="h5"
                  style={{ color: colors.redAccent[400] }}
                >
                  Rejected
                </Typography>
              </div>
            )}
          </>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col justify-center items-center fixed left-0 top-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] z-[3]">
      <div
        style={
          isNonMobile ? { width: "870px" } : { width: screenSize.width + "px" }
        }
        className="rounded scrollbar overflow-scroll p-[10px] bg-[#fff] border-[#eee]"
      >
        <div className="p-[10px] flex justify-between">
          <Typography variant="h3" color={colors.primary[500]}>
            Proposals
          </Typography>
          <Typography
            className="hover:cursor-pointer"
            variant="h3"
            color={colors.primary[500]}
            onClick={() => {
              setIsModalVisible(false);
            }}
          >
            X
          </Typography>
        </div>
        <div>
          <Box
            className="w-[840px]"
            m={"20px 0 0 0"}
            sx={{
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .name-column--cell": {
                color: colors.greenAccent[300],
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.blueAccent[700],
                borderBottom: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: colors.primary[400],
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "noone",
                backgroundColor: colors.blueAccent[700],
              },
              "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${colors.grey[500]} !important`,
              },
            }}
          >
            <DataGrid
              rows={newProposals}
              /*@ts-ignore */
              columns={columns}
              autoHeight={true}
              components={{ Toolbar: GridToolbar }}
              getRowId={(row) => row._id}
            />
          </Box>
        </div>
      </div>
    </div>
  );
};

export default ShowProposalsModal;
